import { useCallback, useEffect, useRef } from 'react'
import { api } from '~/data/api'
import { STATE_STORAGE_LOCATION } from '~/utils/consts'
import { useAuth } from './useAuth'

function HeartbeatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const sendHeartbeat = useCallback(async () => {
    if (!sessionStorage.getItem(STATE_STORAGE_LOCATION) || !user?._id) return

    await api().post(`/user/${user._id}/heartbeat`, { device: 'web' })
  }, [user?._id])

  const startHeartbeat = useCallback(() => {
    if (!intervalRef.current) {
      sendHeartbeat()
      intervalRef.current = setInterval(sendHeartbeat, 60 * 1000) // 1 min
    }
  }, [sendHeartbeat])

  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  useEffect(() => {
    const handleFocus = () => startHeartbeat()
    const handleBlur = () => stopHeartbeat()

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    if (document.visibilityState === 'visible') {
      startHeartbeat()
    }

    return () => {
      stopHeartbeat()
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [startHeartbeat, stopHeartbeat])

  return <>{children}</>
}

export function useHeartbeatProvider() {
  return { HeartbeatProvider }
}
